import { withSearch } from '@elastic/react-search-ui'
import { useStoreContext } from 'context/StoreProvider'
import { Col, Nav, Tab } from 'react-bootstrap'
import {
	chapterString,
	collectionsString,
	searchString,
	historyString,
} from 'utils'
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { TabWrapper } from './style'
import { useTranslation } from 'react-i18next'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faClose, faSitemap } from '@fortawesome/free-solid-svg-icons'
import { isMobile } from 'react-device-detect'
import { HistoryList } from 'components/search/history'
import { useTheme } from 'styled-components'
import { CodeSystem } from 'context/elasticsearch/types'
import CodeSystemBrowserSection from '../CodeSystemBrowserSection'
import {
	ClinicalCodeSystemPanel,
	KisCodeSystemPanel,
} from 'components/CodeSystemPanel'
import CodeSystemSearchResultSection from '../CodeSystemSearchResultSection'
import { useNestedFieldSearch } from 'context/elasticsearch/nestedFields/useNestedFieldSearch'

interface CustomProps {
	codeSystem: CodeSystem
	displayScore: boolean
}

interface SearchProps {
	totalResults: number
	resultSearchTerm: string
	searchTerm?: string
	resultsPerPage?: number
	setResultsPerPage: (value: number) => void
}

const TabView: React.FC<CustomProps & SearchProps> = ({
	totalResults,
	resultSearchTerm,
	searchTerm,
	resultsPerPage,
	setResultsPerPage,
	codeSystem,
	displayScore = false,
}) => {
	const { state } = useStoreContext()
	const { code } = state
	const { view } = useParams()
	const navigate = useNavigate()
	const { t } = useTranslation()

	const theme = useTheme()

	const { value: nestedSearchEnabled, updateValue: updateNestedSearchEnabled } =
		useNestedFieldSearch(
			codeSystem, // Use codeSystem as the key
			false // Default to false if not set
		)

	// IMPORTANT: This useEffect must exist in a parent to the search results
	// It was placed here because it already used HOC parameters from Search UI.
	useEffect(() => {
		// Ensure nested field search is on for ICD-10, ICPC-2, NORPAT, NKPK, PHBU
		if (
			(codeSystem === CodeSystem.Icd10 ||
				codeSystem === CodeSystem.Icpc2 ||
				codeSystem === CodeSystem.Norpat ||
				codeSystem === CodeSystem.Nkpk ||
				codeSystem === CodeSystem.Phbu) &&
			!nestedSearchEnabled
		) {
			updateNestedSearchEnabled(true)
		}

		// When nested search is enabled, set results per page to its current value in order to force a new search
		setResultsPerPage(resultsPerPage || 20)
	}, [nestedSearchEnabled, codeSystem, setResultsPerPage])

	const isKis = codeSystem === CodeSystem.Adm

	const [dropdownHierarchy, setDropdownHierarchy] = useState(true)
	const [chaperView, setChaperView] = useState(true)
	let smallScreen = isMobile || window.innerWidth < 992

	const updateWindowDimensions = () => {
		setDropdownHierarchy(window.innerWidth < 992 ? true : false)
		smallScreen = isMobile || window.innerWidth < 992
	}
	window.addEventListener('resize', updateWindowDimensions)

	const browserString = isKis ? collectionsString : chapterString

	const browserTabName = isKis
		? t('resultPage.collections')
		: t('general.treeView')

	const historyTabName = isKis
		? t('adminPage.historyView')
		: t('general.historyView')

	const handleSetActiveTab = (newActiveKey: string) => {
		if (view === newActiveKey) {
			return null
		} else {
			navigate(newActiveKey)
		}
	}

	useEffect(() => {
		// get search from url to check if it matches, if it does we do not navigate to search but show the searchterm that we brought from our previous location in the searchbar
		const search = window.location.search?.match(/\?q=(\w+)/)
		const hasSearchresultsWithMatchOrNoSearch = search
			? search[1] !== searchTerm
			: true
		if (view === undefined) {
			setChaperView(true)
			navigate(browserString)
		} else if (view === browserString) {
			setChaperView(true)
		} else {
			setChaperView(false)
		}
		if (
			searchTerm !== resultSearchTerm &&
			hasSearchresultsWithMatchOrNoSearch &&
			view === browserString
		) {
			navigate(searchString)
			setChaperView(false)
		}
	}, [view, searchTerm])

	const tabs = [
		{
			key: browserString,
			label: browserTabName,
			ariaLabel: `${(t('general.navigateTo'), t('general.treeView'))}`,
			content: (
				<CodeSystemBrowserSection
					dropdownHierarchy={dropdownHierarchy}
					codeSystem={codeSystem}
					results={
						isKis ? (
							<KisCodeSystemPanel codeSystemId={code} />
						) : (
							<ClinicalCodeSystemPanel />
						)
					}
				/>
			),
		},
		{
			key: searchString,
			label: `${t('general.searchView')}${
				totalResults > 0 && resultSearchTerm ? ` (${totalResults})` : ''
			}`,
			ariaLabel: `${(t('general.navigateTo'), t('general.searchView'))}`,
			content: (
				<CodeSystemSearchResultSection
					codeSystem={codeSystem}
					displayScore={displayScore}
				/>
			),
		},
		{
			key: historyString,
			label: historyTabName,
			ariaLabel: `${(t('general.navigateTo'), historyTabName)}`,
			content: <HistoryList />,
		},
	]

	return (
		<>
			<Tab.Container activeKey={view} onSelect={(k) => navigate(k as string)}>
				<TabWrapper className='flex-column'>
					<Col
						id={`tab-view-${codeSystem}`}
						className='tab-bar d-flex flex-row flex-nowrap overflow-auto justify-content-between'
					>
						<Nav tabIndex={0} className='flex-row flex-nowrap flex-shrink-0'>
							{tabs.map(({ key, label, ariaLabel }) => (
								<Nav.Item
									key={key}
									onClick={() => handleSetActiveTab(key)}
									onKeyDown={(e) => {
										if (e.key === 'Enter' || e.key === ' ') {
											handleSetActiveTab(key)
										}
									}}
									aria-label={ariaLabel}
								>
									<Nav.Link eventKey={key}>{label}</Nav.Link>
								</Nav.Item>
							))}
						</Nav>
					</Col>
					<Col className='p-0'>
						<Tab.Content>
							{/* Start Mobile hierarchy button */}
							{chaperView && smallScreen && (
								<div
									style={{
										position: 'relative',
										height: '0',
										width: '0',
										float: 'right',
									}}
								>
									<button
										style={{
											height: '3rem',
											width: '3rem',
											borderRadius: '6px',
											position: 'absolute',
											right: '0.125em',
											top: '0.125em',
											border: 'none',
											zIndex: 3,
											backgroundColor: dropdownHierarchy
												? theme.colours.secondary.red
												: theme.colours.secondary.Secondary700,
										}}
										onClick={() => setDropdownHierarchy(!dropdownHierarchy)}
									>
										{dropdownHierarchy ? (
											<FontAwesomeIcon icon={faClose} size='sm' color='white' />
										) : (
											<FontAwesomeIcon
												icon={faSitemap}
												size='sm'
												color='white'
											/>
										)}
									</button>
								</div>
							)}
							{/* End Mobile hierarchy button */}
							{tabs.map(({ key, content }) => (
								<Tab.Pane eventKey={key} tabIndex={0} key={key}>
									{content}
								</Tab.Pane>
							))}
						</Tab.Content>
					</Col>
				</TabWrapper>
			</Tab.Container>
		</>
	)
}

export default withSearch<CustomProps, SearchProps>(
	({
		totalResults,
		resultSearchTerm,
		searchTerm,
		resultsPerPage,
		setResultsPerPage,
	}) => ({
		totalResults,
		resultSearchTerm,
		searchTerm,
		resultsPerPage,
		setResultsPerPage,
	})
)(TabView)
